/**
 * @file Classe de base des composants UI : element racine, cycle mount/update/destroy, abonnements bus/DOM auto-nettoyes.
 */
import type { GameEvents } from '@core/index';
import type { UiContext } from '../UiContext';

export abstract class Component {
  readonly el: HTMLElement;
  private readonly disposers: Array<() => void> = [];
  private mounted = false;

  constructor(
    protected readonly ctx: UiContext,
    tag: keyof HTMLElementTagNameMap = 'div',
    className = '',
  ) {
    this.el = document.createElement(tag);
    if (className) this.el.className = className;
  }

  /** Construit le DOM interne (appele une fois au montage). */
  protected abstract build(): void;

  /** Rafraichit les valeurs affichees (appele par UiApp a ~10 Hz pour les ecrans, ~30 Hz pour le HUD). */
  update(_dt: number): void {}

  mount(parent: HTMLElement): this {
    if (!this.mounted) {
      this.build();
      this.mounted = true;
    }
    parent.append(this.el);
    return this;
  }

  /** Abonnement au bus du core, desabonne a destroy(). */
  protected listen<K extends keyof GameEvents>(type: K, fn: (p: GameEvents[K]) => void): void {
    this.disposers.push(this.ctx.sim.bus.on(type, fn));
  }

  /** Ecouteur DOM nettoye a destroy(). */
  protected on<K extends keyof HTMLElementEventMap>(target: HTMLElement | Window | Document, type: K, fn: (e: HTMLElementEventMap[K]) => void, opts?: AddEventListenerOptions): void {
    target.addEventListener(type, fn as EventListener, opts);
    this.disposers.push(() => target.removeEventListener(type, fn as EventListener, opts));
  }

  protected addDisposer(fn: () => void): void {
    this.disposers.push(fn);
  }

  destroy(): void {
    for (const d of this.disposers.splice(0)) d();
    this.el.remove();
  }
}
